/**
 * Embeds: Tadpole pages in a frame on a business's own website. Booking
 * widgets live under /embed/ and run without the login; the website inquiry
 * form (/f/inquiry/) is put on websites the same way.
 */
export const EMBED_PATH = "/embed";

/** The empty element at a widget's top, brought into view on a new step. */
export const EMBED_TOP_ID = "tadpole-embed-top";

// The messages between a frame and the code pasted around it.
/** Frame to website: how tall the frame's page is. */
export const EMBED_HEIGHT_MESSAGE = "tadpole-embed-height";
/** Frame to website: bring the frame's top into view. */
export const EMBED_TOP_MESSAGE = "tadpole-embed-top";
/** Website to frame: say your height again (the code may attach late). */
export const EMBED_HELLO_MESSAGE = "tadpole-embed-hello";

/** This page is a booking widget. */
export function inEmbed(): boolean {
  return window.location.pathname.startsWith(`${EMBED_PATH}/`);
}

/**
 * Ask the website around this frame to bring the frame's top into view. A
 * frame's own scrollIntoView reaches the website in Chrome but not in WebKit
 * (Safari, and every browser on an iPhone), so the embed code does it, and
 * only when the top is out of sight.
 */
export function askWebsiteToShowTop() {
  window.parent.postMessage({ type: EMBED_TOP_MESSAGE }, "*");
}

/**
 * In a widget, bring its top into view, as a page of its own would start at
 * the top. Scrolls the website only if the top is out of sight. Elsewhere,
 * nothing.
 */
export function revealTop() {
  if (!inEmbed()) return;
  document.getElementById(EMBED_TOP_ID)?.scrollIntoView({ block: "nearest" });
  askWebsiteToShowTop();
}

/**
 * What a business pastes into its website: a Tadpole page in a frame, and a
 * short script. The script listens to Tadpole's origin only. It sizes each
 * Tadpole frame by that frame's height messages (found by the message's
 * source, so two copies on one page each grow), and brings a frame's top
 * into view when the frame asks and the top is out of sight. Once it runs it
 * says hello to the frames, so one that loaded before a late script (speed
 * plugins hold scripts back) sends its height again.
 */
export function frameCode({
  src,
  title,
  height,
  maxWidth,
}: {
  src: string;
  title: string;
  height: number;
  maxWidth?: number;
}): string {
  const origin = new URL(src).origin;
  const escaped = title.replace(/[&"<>]/g, (c) => `&#${c.charCodeAt(0)};`);
  const width = maxWidth ? `width:100%;max-width:${maxWidth}px` : "width:100%";
  return [
    `<iframe src="${src}" title="${escaped}"`,
    `  style="${width};height:${height}px;border:0" loading="lazy"></iframe>`,
    `<script>`,
    `(function () {`,
    `  var origin = "${origin}";`,
    `  var frames = document.getElementsByTagName("iframe");`,
    `  function frameOf(source) {`,
    `    for (var i = 0; i < frames.length; i++) if (frames[i].contentWindow === source) return frames[i];`,
    `  }`,
    `  window.addEventListener("message", function (e) {`,
    `    var frame = e.origin === origin && e.data && frameOf(e.source);`,
    `    if (!frame) return;`,
    `    if (e.data.type === "${EMBED_HEIGHT_MESSAGE}") frame.style.height = e.data.height + "px";`,
    `    if (e.data.type === "${EMBED_TOP_MESSAGE}" && frame.getBoundingClientRect().top < 0) frame.scrollIntoView({ block: "start" });`,
    `  });`,
    `  for (var i = 0; i < frames.length; i++) {`,
    `    if (frames[i].src.indexOf(origin + "/") === 0 && frames[i].contentWindow)`,
    `      frames[i].contentWindow.postMessage({ type: "${EMBED_HELLO_MESSAGE}" }, origin);`,
    `  }`,
    `})();`,
    `</script>`,
  ].join("\n");
}

/**
 * A booking widget's code, on Tadpole's own domain. `path` is the page under
 * /embed, such as "/organizations/12".
 */
export function widgetCode(path: string, title: string): string {
  return frameCode({
    src: `${process.env.REACT_APP_FRONTEND_DOMAIN}${EMBED_PATH}${path}`,
    title,
    height: 760,
  });
}
